import { cn } from "@repo/ui/lib/utils";
import { Link as LinkIcon } from "lucide-react";
import type { ReactNode } from "react";

interface ConcurrentProps {
  children: ReactNode;
  className?: string;
}

/**
 * Two or more procedures that run concurrently within a protocol step. Serial
 * (single-procedure) steps use none of these.
 */

/** A right-side brace hugging narrow rows that keep left-hand tree connectors. */
export function ConcurrentBrace({ children, className }: ConcurrentProps) {
  return (
    <div className={cn("flex w-fit max-w-full items-stretch gap-2", className)}>
      <div className="flex min-w-0 flex-col">{children}</div>
      <div
        aria-hidden
        className="my-1 w-1.5 shrink-0 rounded-r-[5px] border-muted-foreground/25 border-y-2 border-r-2"
      />
      <span className="flex shrink-0 items-center font-medium text-[10px] text-muted-foreground/60">Concurrent</span>
    </div>
  );
}

/** A tinted band with the label on top, for full-width rows. */
export function ConcurrentBar({ children, className }: ConcurrentProps) {
  return (
    <div className={cn("rounded-lg bg-muted/40 px-2.5 pb-2", className)}>
      <div className="flex items-center gap-1.5 pt-1.5 font-medium text-[10px] text-muted-foreground/70 uppercase tracking-wide">
        <LinkIcon aria-hidden className="size-3" />
        Concurrent
      </div>
      <div className="flex flex-col">{children}</div>
    </div>
  );
}

/** The label alone, for a stacked lane that is already vertically grouped. */
export function ConcurrentLabel() {
  return (
    <span className="flex items-center gap-1 text-[10px] text-muted-foreground uppercase tracking-wide">
      <LinkIcon aria-hidden className="size-2.5" />
      Concurrent
    </span>
  );
}
